import React, { useState, useEffect } from 'react';

import type MarketLog from '@this/domain/market_log/market_log';
import { Text } from '@this/shared/ui/data_displays/typography';
import { Select } from '@this/shared/ui/inputs/select';
import { Button } from '@this/shared/ui/inputs/button';
import { formatPrice } from '@this/src/util/price/price.util';
import Tooltip from '@this/shared/tooltip/tooltip';
import MarketLogSample from './market_log_sample';

type Props = {
  marketLog: MarketLog;
  available: boolean;
};

const MarketLogTemplate: React.FC<Props> = ({ marketLog, available }) => {
  const [sortBy, setSortBy] = useState<'price' | 'distance'>('price');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [sortedCandidates, setSortedCandidates] = useState(marketLog.candidates || []);

  useEffect(() => {
    const candidates = marketLog.candidates || [];
    // 初期表示時に価格・昇順でソート
    const sorted = [...candidates].sort((a, b) => a.price - b.price);
    setSortedCandidates(sorted);
  }, [marketLog.candidates]);

  const handleSearch = () => {
    if (!marketLog.candidates || marketLog.candidates.length === 0) {
      return;
    }

    const sorted = [...marketLog.candidates].sort((a, b) => {
      let comparison = 0;

      if (sortBy === 'price') {
        comparison = a.price - b.price;
      } else if (sortBy === 'distance') {
        const aDistance = a.distance;
        const bDistance = b.distance;
        comparison = aDistance - bDistance;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    setSortedCandidates(sorted);
  };

  return (
    <div className="market-log__body">
      <div style={{ marginTop: '10px', fontWeight: 'bold' }}>
        <h2 style={{ fontWeight: 'bold' }}>基本情報</h2>
        <div style={{ display: 'flex' }}>
          <div style={{ flexGrow: 1 }}>
            <div style={getStyles().infoRow}>
              <p style={getStyles().infoLabel}>予約者</p>
              <p style={getStyles().infoCell}>{marketLog.userName}</p>
            </div>
            <div style={getStyles().infoRow}>
              <p style={getStyles().infoLabel}>承認者</p>
              <p style={getStyles().infoCell}>{marketLog.approverNames}</p>
            </div>
            <div style={getStyles().infoRow}>
              <p style={getStyles().infoLabel}>出張者</p>
              <p style={getStyles().infoCell}>{marketLog.travelerNames}</p>
            </div>
          </div>
          <div style={{ flexGrow: 1 }}>
            <div style={getStyles().infoRow}>
              <p style={getStyles().infoLabel}>宿泊日</p>
              <p style={getStyles().infoCell}>{marketLog.term}</p>
            </div>
            <div style={getStyles().infoRow}>
              <p style={getStyles().infoLabel}>施設名</p>
              <p style={getStyles().infoCell}>{marketLog.name}</p>
            </div>
            <div style={getStyles().infoRow}>
              <p style={getStyles().infoLabel}>料金</p>
              <p style={getStyles().infoCell}>{marketLog.priceDesc}</p>
            </div>
            <div style={getStyles().infoRow}>
              <p style={getStyles().infoLabel}>目的地からの距離</p>
              <p style={getStyles().infoCell}>{marketLog.distanceDesc()}</p>
            </div>
            {marketLog.address && (
              <div style={getStyles().infoRow}>
                <p style={getStyles().infoLabel}>目的地</p>
                <p style={getStyles().infoCell}>{marketLog.address}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {available ? (
        <>
          <div style={{ marginTop: '10px' }}>
            <h2 style={{ fontWeight: 'bold' }}>サマリー</h2>
            <div style={{ display: 'flex', justifyContent: 'space-around' }}>
              <div style={getStyles().summaryBox}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                  <p style={{ fontSize: '0.75em' }}>旅費規程</p>
                  <Tooltip type="info" width={250}>
                    管理画面で設定した旅費規程を表示しています。
                  </Tooltip>
                </div>
                <Text bold style={{ fontSize: '1.3em' }}>
                  {marketLog.hotelPriceLimitDesc()}
                </Text>
              </div>
              <div style={getStyles().summaryBox}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                  <p style={{ fontSize: '0.75em' }}>当日の平均</p>
                  <Tooltip type="info" width={250}>
                    申請時に空室があったものの平均価格。
                  </Tooltip>
                </div>
                <Text bold style={{ fontSize: '1.3em' }}>
                  {formatPrice(marketLog.averagePrice)}
                </Text>
              </div>
              <div style={getStyles().summaryBox}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                  <p style={{ fontSize: '0.75em' }}>申請時の価格</p>
                  <Tooltip type="info" width={250}>
                    予約者が申請したタイミングでの選択したホテルの価格。
                  </Tooltip>
                </div>
                <Text bold style={{ fontSize: '1.3em' }}>
                  {formatPrice(marketLog.priceOrdered)}
                </Text>
              </div>
              <div style={getStyles().summaryBox}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                  <p style={{ fontSize: '0.75em' }}>旅費規程との差額</p>
                  <Tooltip type="info" width={250}>
                    申請時の価格と旅費規程との差額を計算しています。
                  </Tooltip>
                </div>
                <Text
                  bold
                  color={marketLog.isPriceLimitDiffPlus() ? 'danger' : 'success'}
                  style={{ fontSize: '1.3em' }}
                >
                  {marketLog.priceLimitDiffDesc()}
                </Text>
              </div>
              <div style={getStyles().summaryBox}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                  <p style={{ fontSize: '0.75em' }}>申請時からの価格上昇</p>
                  <Tooltip type="info" place="left" width={280}>
                    選択したホテルにまだ空室があり、価格が上昇している場合は＋～円と表示されます。
                    売り切れの場合は0と表示されます。
                  </Tooltip>
                </div>
                <Text
                  bold
                  color={marketLog.isPriceIncreasesPlus() ? 'danger' : 'success'}
                  style={{ fontSize: '1.3em' }}
                >
                  {marketLog.priceIncreasesDesc()}
                </Text>
              </div>
            </div>
          </div>
          <div style={{ marginTop: '10px' }}>
            <h2 style={{ fontWeight: 'bold' }}>その他の候補</h2>
            {sortedCandidates && sortedCandidates.length > 0 ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'flex-end',
                    marginBottom: '16px',
                    justifyContent: 'flex-end'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span
                      style={{
                        fontSize: '12px',
                        color: '#666',
                        marginBottom: '4px',
                        marginLeft: '-80px'
                      }}
                    >
                      並び順
                    </span>
                    <Select
                      value={sortBy}
                      onChange={e => setSortBy(e.target.value as 'price' | 'distance')}
                      size="medium"
                      style={{ fontWeight: 'bold', fontSize: '13px', minWidth: '120px' }}
                    >
                      <option value="price">価格</option>
                      <option value="distance">目的地からの距離</option>
                    </Select>
                  </div>
                  <Select
                    value={sortOrder}
                    onChange={e => setSortOrder(e.target.value as 'asc' | 'desc')}
                    size="medium"
                    style={{ fontWeight: 'bold', fontSize: '14px', minWidth: '80px' }}
                  >
                    <option value="asc">昇順</option>
                    <option value="desc">降順</option>
                  </Select>
                  <Button onClick={handleSearch} size="medium">
                    検索
                  </Button>
                </div>
                <table style={{ borderCollapse: 'separate', borderSpacing: '4px 0', tableLayout: 'fixed' }}>
                  <tbody>
                    <tr>
                      <th style={{ ...getStyles().tableHeader, width: '60%' }}>施設名</th>
                      <th style={getStyles().tableHeader}>価格</th>
                      <th style={getStyles().tableHeader}>目的地からの距離</th>
                    </tr>
                    {sortedCandidates.map((c, index) => {
                      // 一意のキーを生成（名前、価格、距離、インデックスを組み合わせてハッシュ化）
                      const uniqueKey = `${c.name}_${c.price}_${c.distance}_${index}`.replace(
                        /[^a-zA-Z0-9_]/g,
                        '_'
                      );
                      return (
                        <tr key={uniqueKey}>
                          <td>{c.name}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>{formatPrice(c.price)}</span>
                              <Text
                                color={c.isPriceDiffPlus(marketLog.priceOrdered) ? 'danger' : 'success'}
                                level="caption"
                                bold
                              >
                                ({c.priceDiffDesc(marketLog.priceOrdered)})
                              </Text>
                            </div>
                          </td>
                          <td>{c.distanceDesc()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            ) : (
              <p>その他の候補が見つかりませんでした</p>
            )}
          </div>
        </>
      ) : (
        <MarketLogSample />
      )}
    </div>
  );
};

function getStyles() {
  return {
    infoRow: {
      display: 'flex',
      fontSize: '0.75em'
    } as React.CSSProperties,
    infoLabel: {
      flexBasis: '100px',
      backgroundColor: '#f7f7f7',
      textAlign: 'center',
      padding: '5px 20px'
    } as React.CSSProperties,
    infoCell: {
      padding: '5px'
    } as React.CSSProperties,
    summaryBox: {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: '100%',
      textAlign: 'center',
      backgroundColor: '#f7f7f7',
      padding: '10px 30px',
      margin: '0 2px'
    } as React.CSSProperties,
    tableHeader: {
      textAlign: 'center',
      fontSize: '0.75em',
      backgroundColor: '#f7f7f7'
    } as React.CSSProperties
  } as const;
}

export default MarketLogTemplate;
